import moment from 'moment-timezone';
import { formatPhysicianName } from '@mummssoftware/utils/formatters';
import { physicianOptionNames } from '@mummssoftware/utils/utils';
import { PSO } from '@mummssoftware/utils/constants';
import {
  ATTENDING_FORM,
  CERTIFYING_FORM,
  NO_ATTENDING,
  RESTRICTED_ROLES,
  MILLISECONDS_IN_DAY,
} from './constants';

export const getFormPhys = (phys, id) => {
  let formPhys;
  if (phys.id === id) {
    formPhys = {
      // OP-33278: picker shows "Last, First, Cred"; fullName keeps the salutation form
      ...physicianOptionNames(phys.firstName, phys.lastName, phys.credentials),
      fullName: formatPhysicianName(
        phys.salutation,
        phys.firstName,
        phys.lastName,
        phys.credentials,
      ),
      value: phys.id,
      npi: phys?.NPI,
    };
  }
  return formPhys;
};

export const getFormTypeKeys = (cert, formType) => {
  if (formType === ATTENDING_FORM) {
    return {
      formType,
      physKey: 'attendingPhys',
      fallbackPhysId: cert.episode.attendingPhysicianId,
    };
  }
  return {
    formType,
    physKey: 'hospicePhys',
  };
};

export const processCert = async ({
  cert,
  certIndex,
  checkRoles,
  currentEpisode,
  electionNumber,
  fallbackPhysId,
  formPhysRestricted = false,
  formType,
  notInEpisode = currentEpisode !== cert?.episode?.number,
  physKey,
  pim,
}) => {
  // TODO: add defaultDate: admitted time if electionNumber === 1
  const formPhysId = cert[physKey].id || fallbackPhysId;
  let defaultDate = null;
  if (electionNumber === 1) {
    const start = moment(cert.startDate);
    const referralDate = moment(cert?.episode?.referralDate);
    if (
      start.isSame(referralDate, 'day') ||
      start.isSame(moment(cert.episode.admitDate), 'day')
    ) {
      defaultDate = referralDate.toDate();
    }
  }
  if (
    pim &&
    checkRoles &&
    typeof formPhysId === 'number' &&
    formPhysId !== NO_ATTENDING
  ) {
    /* eslint-disable no-console, no-param-reassign */
    const roles = await pim
      .get(`/web/api/persons/${formPhysId}/roles`)
      .catch(console.error);
    formPhysRestricted =
      /* eslint-enable */
      (roles &&
        roles.data &&
        !!roles.data.filter(({ shortName }) =>
          RESTRICTED_ROLES.includes(shortName),
        ).length) ||
      formPhysRestricted;
  }
  const { startDate, actualEndDate, expectedEndDate } = cert;
  return {
    complete: !!cert[`${physKey}WrittenDate`],
    verbalComplete: !!cert[`${physKey}VerbalDate`],
    certIndex,
    defaultDate,
    electionNumber,
    formPhys: getFormPhys(cert[physKey], formPhysId),
    formPhysId,
    formPhysRestricted,
    formType,
    notInEpisode,
    cert,
    startDate,
    endDate: actualEndDate || expectedEndDate,
  };
};

export const processCerts = async (certs, pim) => {
  const currentEpisode = certs;
  return certs.reduce(async (previousForms, cert, certIndex) => {
    const forms = await previousForms;
    const newForms = [];
    const { electionNumber } = cert;
    const { showattending } = cert;
    const certForm = await processCert({
      cert,
      certIndex,
      currentEpisode,
      electionNumber,
      ...getFormTypeKeys(cert, CERTIFYING_FORM),
    });
    newForms.push(certForm);
    if (electionNumber === 1 || showattending) {
      const attdForm = await processCert({
        cert,
        certIndex,
        checkRoles: true,
        currentEpisode,
        electionNumber,
        ...getFormTypeKeys(cert, ATTENDING_FORM),
        pim,
      });
      newForms.push(attdForm);
    }
    forms.push(...newForms);
    return forms;
  }, Promise.resolve([]));
};

export const formatDate = (date) => {
  const isoDate = date.toISOString().split('T');
  return `${isoDate[0]} ${isoDate[1].slice(0, 10)}`;
};

export const formatMMDDYY = (date, tz) => {
  if (date && tz) {
    return moment.utc(date).tz(tz).format('MM/DD/YY');
  }
  return '';
};

// helper function to determine if an approval needs a PSO from orders and notes
// if so returns the entity key for observation else return null
export const notesHavePSO = (approval, notes) => {
  if (
    approval.entityordertypeshortname === PSO &&
    notes.patientprogressnoteentitydto
  ) {
    const psoEntity = notes.patientprogressnoteentitydto.find(
      (note) => note.entitytypeshortname === PSO,
    );
    if (psoEntity) return psoEntity.entitykey;
  }
  return null;
};

export const noteHasPSO = (approval, note) => {
  if (
    approval.entityordertypeshortname === PSO &&
    note.patientprogressnoteentitydto &&
    note.patientprogressnoteentitydto.PSO
  ) {
    return note.patientprogressnoteentitydto.PSO[0].entitykey;
  }
  return null;
};

/**
 * Helper function that will calculate the date X days prior to the passed in Date and.
 * return the ISO String of the calculated date. If no date is passed in, today's date is used.
 * @param {*} days number of days to subtracked from date.
 * @param {*} date date to count back from.
 * @returns ISO String format of the calculated Date.
 */
export const getDateXDaysBeforeDateAsISOString = (days, date = new Date()) => {
  const daysBeforeInMs = days * MILLISECONDS_IN_DAY;
  const daysBefore = new Date(date.getTime());

  daysBefore.setTime(date.getTime() - daysBeforeInMs);
  return daysBefore.toISOString();
};

// Helper function to determine if user's agency has migrated to new med-orders workflow
const hideColumnsFor = window.env.medOrdersLiveList;
export const hideMedColumn = (agency) =>
  hideColumnsFor.includes('*') ||
  hideColumnsFor.includes(agency) ||
  hideColumnsFor === agency;

/**
 * Filter the certs when printing. When printing certifications, we need to be able
 * to filter the forms (cert cards) by which forms benefit period is within the
 * printing date range.  OP-29783
 *
 * For example: print date range 2/1/23 - 6/15/23
 * certs: bp1: 1/1/23 - 3/31/23
 *        bp2: 4/1/23 - 5/31/23
 *        bp3: 6/1/23 - 7/21/23
 *
 * bp 1,2, and 3 would be selected because the benefit periods of each cert is within
 * the print daterange.
 */
export const filterByBenefitStartDateFunc = (
  start,
  end,
  benefitStartMoment,
  benefitEndMoment,
) => {
  if (!benefitStartMoment || !benefitEndMoment || !start || !end) {
    return null;
  }

  return (
    benefitStartMoment.isSameOrBefore(end, 'day') &&
    benefitEndMoment.isSameOrAfter(start, 'day')
  );
};

/**
 * Given the passed in patientCertHistory, this function does will generate a
 * list of cert forms in chronoligical order from oldest to newest.
 */
export const convertPatientCertHistoryToCertForms = async (
  patientCertHistory,
  pim,
) => {
  if (!patientCertHistory || !pim) return null;

  const dateComparator = (plan1, plan2) =>
    new Date(plan1.startDate).getTime() - new Date(plan2.startDate).getTime();

  const currentCertHistory = Object.entries(patientCertHistory).reduce(
    (seed, value) => seed.concat(value[1]),
    [],
  );

  // sort the certs by start time, oldest to newest
  currentCertHistory.sort(dateComparator);
  const certForms = await processCerts(currentCertHistory, pim);

  return certForms;
};
